import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { isApiUrl } from '../http/api-url';
import { ViewAsService } from '../services/view-as.service';

/** Error code of the refusal THIS interceptor raises, in the browser, for a write attempted while
 *  the viewpoint's editing toggle is off. It is not a BE code any more (F9 revision, PO
 *  2026-09-19): the BE never sees a mode. Kept as the FE's own constant so the screens that map
 *  `error.error` -> `errors.<CODE>` and the `errors.VIEW_AS_READ_ONLY` toast (which points at the
 *  banner's "Enable editing") keep working unchanged. */
export const VIEW_AS_READ_ONLY = 'VIEW_AS_READ_ONLY';
/** The BE refuses the header from anyone who is not an allow-listed platform admin. */
export const VIEW_AS_FORBIDDEN = 'VIEW_AS_FORBIDDEN';
/** The BE's answer to a header naming a group that no longer exists. */
export const GROUP_NOT_FOUND = 'GROUP_NOT_FOUND';

const READ_METHODS = new Set(['GET', 'HEAD']);

/**
 * Admin "open as this group" (F7, handoff §17; write mode F9, story #3362, revised by the PO on
 * 2026-09-19): while a viewpoint is pinned, every request that leaves the browser for
 * be-hotel-extranet-service carries `X-Extranet-View-As-Group: <groupId>` — and nothing else.
 * Registered after `authInterceptor` in app.config.ts, so the header rides next to the bearer
 * token on the same request.
 *
 * Authorisation lives in the TOKEN, not in a header the FE could set: the BE verifies the bearer
 * token's signature itself (quarkus-oidc) and, when its email is in the platform_admin allowlist,
 * runs ANY request — read or write — as the viewed group, exactly as that group's own manager
 * would, attributed to the admin's email and audited (`admin_view_as_audit`: method, path,
 * outcome, JSON body of an admitted write, response status). A non-admin with the header gets
 * 403 `VIEW_AS_FORBIDDEN`; a header naming a group that is gone gets 404 `GROUP_NOT_FOUND`. The
 * BE knows no "mode" and no `VIEW_AS_READ_ONLY` — there is no `X-Extranet-View-As-Mode` header,
 * and none must ever be sent.
 *
 * The read-only default and the banner's "Enable editing" toggle (+ confirmation) are a
 * BROWSER-SIDE SAFEGUARD, decided by `ViewAsService.writeMode` (per tab):
 *
 * - **Toggle off** (how every viewpoint starts): a non-read request is refused HERE and never
 *   sent — a synthesised 403 `{ error: VIEW_AS_READ_ONLY, messages: [<toast text>] }` plus the
 *   `errors.VIEW_AS_READ_ONLY` toast, opened once from the interceptor so that screens which
 *   swallow or genericise a save error (calendar bulk save, logo upload, order sources) are never
 *   silent; screens that print the BE's `messages[]` show the same explanation inline. The same
 *   refusal applies whichever host the screen talks to (a presigned upload URL included):
 *   nothing can be written from a read-only viewpoint. Write buttons stay visible on purpose —
 *   what the operator would see is the point of the viewpoint, and the toast says where editing
 *   is switched on. A write blocked in the browser leaves NO audit row: by design, the BE only
 *   audits what reaches it, and nothing did.
 * - **Toggle on** (after the banner's confirmation): a write to the extranet BE goes out with
 *   just the group header, and the BE decides and audits; a write to another host goes out
 *   untouched (the upload's metadata write that follows it lands on the extranet BE).
 *
 * Reads always pass, with the group header on the extranet BE and untouched elsewhere.
 *
 * A pin that the BE will never honour — the group is gone (404 `GROUP_NOT_FOUND`) or the caller
 * is not an admin (403 `VIEW_AS_FORBIDDEN`) — would otherwise trap the operator: every GET /me
 * fails, the guards land on /unauthorized outside the shell (no banner, no "Back to admin") and
 * the pin survives in sessionStorage. So the first refused READ drops the pin and is retried once
 * without the header, answered as the operator themselves. Never a write: without the header it
 * would run for real as the admin's OWN membership (the seeded admin is also an agency manager).
 * Every other error — a 401 included — passes through untouched and drops nothing: an expired
 * token is the auth interceptor's and the guards' business, and the pin must still be there once
 * the admin is signed in again.
 */
export const viewAsInterceptor: HttpInterceptorFn = (req, next) => {
  const viewAs = inject(ViewAsService);
  const group = viewAs.group();
  if (!group) return next(req);

  const isRead = READ_METHODS.has(req.method.toUpperCase());
  const toExtranetBe = isApiUrl(req.url);

  if (!isRead && !viewAs.writeMode()) {
    const translate = inject(TranslateService);
    const text = translate.instant(`errors.${VIEW_AS_READ_ONLY}`);
    inject(MatSnackBar).open(text, undefined, { duration: 5000, panelClass: 'snack-error' });
    return throwError(() => new HttpErrorResponse({
      status: 403,
      statusText: 'Forbidden',
      url: req.url,
      error: { error: VIEW_AS_READ_ONLY, messages: [text] },
    }));
  }

  if (!toExtranetBe) return next(req);

  return next(withGroupHeader(req, group.id)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
      const code = typeof err.error?.error === 'string' ? err.error.error : null;
      const unusablePin = (err.status === 404 && code === GROUP_NOT_FOUND) || (err.status === 403 && code === VIEW_AS_FORBIDDEN);
      if (unusablePin && isRead) {
        viewAs.clear();
        return next(req);
      }
      return throwError(() => err);
    }),
  );
};

function withGroupHeader<T>(req: HttpRequest<T>, groupId: string): HttpRequest<T> {
  return req.clone({ setHeaders: { [ViewAsService.HEADER]: groupId } });
}
